<template>
    <div class="xeEquipStatusMonitor heightMAX">
        <div class="kbet-custom-reset heightMAX">
            <div class="rounded kbet-widget-status heightMAX" :style="getBackgroundStyle()">
                <!-- 컴팩트 모드 (리스트 형태) - width < 8 -->
                <template v-if="isCompactMode">
                    <div v-for="(group, gIndex) in equipTypeGroups" :key="gIndex" class="equip-type-section-compact">
                        <!-- 설비 유형 뱃지 -->
                        <div class="type-badge-container">
                            <div class="type-badge">
                                <app-icon :icon="getEquipTypeIcon(group.equipType)" class="type-icon" />
                                <span class="type-name">{{ group.equipTypeName }}</span>
                                <span class="equip-count-badge">{{ group.equips.length }}</span>
                            </div>
                        </div>

                        <!-- 설비 리스트 -->
                        <div class="equip-list-container">
                            <div v-for="(equip, eIndex) in group.equips" :key="eIndex" class="equip-list-item">
                                <!-- 설비명 + 상태 -->
                                <div class="equip-list-header">
                                    <span class="equip-list-name">{{ equip.equipName }}</span>
                                    <span class="status-indicator" :class="getOverallStatus(equip.ioProps)"></span>
                                </div>

                                <!-- 속성들 (2열 그리드) -->
                                <div
                                    v-if="equip.ioProps && equip.ioProps.length > 0"
                                    class="equip-list-props"
                                    :style="getPropGridStyleCompact(equip.ioProps.length)"
                                >
                                    <div
                                        v-for="(prop, pIndex) in equip.ioProps"
                                        :key="pIndex"
                                        :class="['prop-tag', 'status-' + prop.status]"
                                        v-b-tooltip.hover
                                        :title="getTooltip(prop)"
                                    >
                                        <span class="prop-tag-label">{{ prop.propName }}</span>
                                        <span class="prop-tag-value">{{ formatPropValue(prop) }}</span>
                                    </div>
                                </div>
                                <div v-else class="no-props-inline">
                                    {{ $t("데이터 없음") }}
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- 타일 모드 (그리드 형태) - width >= 8 -->
                <template v-else>
                    <div v-for="(group, gIndex) in equipTypeGroups" :key="gIndex" class="equip-type-section">
                        <!-- 설비 유형 뱃지 -->
                        <div class="type-badge-container">
                            <div class="type-badge">
                                <app-icon :icon="getEquipTypeIcon(group.equipType)" class="type-icon" />
                                <span class="type-name">{{ group.equipTypeName }}</span>
                                <span class="equip-count-badge">{{ group.equips.length }}</span>
                            </div>
                        </div>

                        <!-- 설비 타일 그리드 -->
                        <div class="equip-tiles-container" :style="getEquipGridStyle(group.equips.length)">
                            <div v-for="(equip, eIndex) in group.equips" :key="eIndex" class="equip-tile">
                                <!-- 설비명 -->
                                <div class="equip-tile-header">
                                    <span class="equip-tile-name">{{ equip.equipName }}</span>
                                    <span class="status-indicator" :class="getOverallStatus(equip.ioProps)"></span>
                                </div>

                                <!-- 속성들 (2열 그리드) -->
                                <div
                                    v-if="equip.ioProps && equip.ioProps.length > 0"
                                    class="prop-list"
                                    :style="getPropGridStyleTile(equip.ioProps.length)"
                                >
                                    <div
                                        v-for="(prop, pIndex) in equip.ioProps"
                                        :key="pIndex"
                                        :class="['prop-item', 'status-' + prop.status]"
                                        v-b-tooltip.hover
                                        :title="getTooltip(prop)"
                                    >
                                        <span class="prop-item-label">{{ prop.propName }}</span>
                                        <span class="prop-item-value">{{ formatPropValue(prop) }}</span>
                                    </div>
                                </div>

                                <!-- 속성 없을 경우 -->
                                <div v-else class="no-props">
                                    <i class="fas fa-info-circle"></i>
                                    <span>{{ $t("표시할 데이터가 없습니다") }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- 설비 없을 경우 -->
                <div v-if="equipTypeGroups.length === 0" class="no-equips">
                    <div class="no-equips-icon">
                        <i class="fas fa-cog"></i>
                    </div>
                    <div class="no-equips-text">
                        {{ $t("설정에서 설비를 선택해주세요") }}
                    </div>
                </div>
            </div>
        </div>

        <!-- 설정 모달 -->
        <b-modal
            ref="modalSetting"
            :title="`${$t('설정')} - ${$t('설비 상태 모니터')}`"
            body-class="p-t-0 p-b-0"
            hide-footer
            size="lg"
            @hidden="onModalCancel"
        >
            <equip-status-monitor-setting
                :initSetting="equipStatusMonitorSetting"
                @on-modal-cancel="onModalCancel"
                @on-modal-ok="onModalOK"
            />
        </b-modal>
    </div>
</template>

<script>
import xeBaseWidget from "@src/views/widget/v1/common/xeBaseWidget.js";
import EquipStatusMonitorSetting from "./setting/xeEquipStatusMonitorSetting";
import { mapState } from "vuex";
import { getEquipTypeIconFromStore } from "@src/utils/iconDisplay.js";

export default {
    name: "xeEquipStatusMonitor",
    extends: xeBaseWidget,
    components: {
        EquipStatusMonitorSetting,
    },
    props: {
        contextMenu: {
            type: Object,
            default: () => {},
        },
        width: {
            type: Number,
            default: 1,
        },
        height: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            equipTypeGroups: [],
            ptAddrMap: {},
        };
    },
    computed: {
        ...mapState({
            units: (state) => state.units,
        }),
        equipStatusMonitorSetting() {
            return JSON.parse(JSON.stringify(this.widget));
        },
        totalReal() {
            return this.$store.state.totalReal;
        },
        // 컴팩트 모드 (width < 8: 리스트, width >= 8: 타일)
        isCompactMode() {
            return this.width < 8;
        },
    },
    watch: {
        // totalReal 변경 시 실시간 업데이트
        "$store.state.totalReal": {
            handler(newTotalReal) {
                if (newTotalReal) {
                    this.updateRealTimeData(newTotalReal);
                }
            },
            deep: true,
        },
    },
    mounted() {
        this.applyData();
    },
    methods: {
        // 저장된 데이터로 UI 구성 (API 호출 없음!)
        applyData() {
            if (!this.widget.selectedEquips || !this.widget.equipStatusData || !this.widget.equipTypeInfoMap) {
                this.equipTypeGroups = [];
                return;
            }

            // 저장된 데이터로 equipTypeGroups 생성
            this.equipTypeGroups = this.buildEquipTypeGroups();

            // ptAddr 매핑 생성
            this.buildPtAddrMap();
        },

        // 저장된 데이터로 표시 데이터 구성
        buildEquipTypeGroups() {
            return this.widget.selectedEquips.map((selection) => {
                const equipTypeInfo = this.widget.equipTypeInfoMap[selection.equipType];

                const equips = selection.equipList
                    .map((equipInfo) => {
                        const equipData = this.widget.equipStatusData[equipInfo.equipIdx];
                        if (!equipData) return null;

                        return {
                            equipIdx: equipData.equipIdx,
                            equipName: equipData.equipName,
                            ioProps: this.filterAndFormatProps(
                                equipData.ioProps,
                                equipInfo.selectedProps, // 선택된 propCode 배열
                                equipTypeInfo
                            ),
                        };
                    })
                    .filter((e) => e !== null);

                return {
                    equipType: selection.equipType,
                    equipTypeName: selection.equipTypeName,
                    equips,
                };
            });
        },

        // 선택된 속성만 필터링
        filterAndFormatProps(ioProps, selectedProps) {
            if (!ioProps || !Array.isArray(ioProps)) {
                return [];
            }

            // 선택된 propCode만 필터링
            const displayProps = ioProps.filter((prop) => selectedProps.includes(prop.propCode));

            return displayProps.map((prop) => ({
                propCode: prop.propCode,
                propName: prop.propName,
                ptVal: prop.ptVal,
                ptAddr: prop.ptAddr,
                unit: prop.unit,
                ptType: prop.ptType,
                status: this.classifyStatus(prop),
            }));
        },

        // ptAddr 매핑 생성
        buildPtAddrMap() {
            const map = {};
            this.equipTypeGroups.forEach((group) => {
                group.equips.forEach((equip) => {
                    equip.ioProps.forEach((prop) => {
                        if (prop.ptAddr) {
                            map[prop.ptAddr] = {
                                equipIdx: equip.equipIdx,
                                propCode: prop.propCode,
                            };
                        }
                    });
                });
            });
            this.ptAddrMap = map;
        },

        // totalReal 변경 시 로컬 데이터만 업데이트 (API 호출 없음!)
        updateRealTimeData(totalReal) {
            Object.keys(totalReal).forEach((ptAddr) => {
                const mapping = this.ptAddrMap[ptAddr];
                if (!mapping) return;

                const newValue = totalReal[ptAddr].ptVal;
                this.updatePropValue(mapping.equipIdx, mapping.propCode, newValue);
            });
        },

        // 특정 속성의 값만 업데이트
        updatePropValue(equipIdx, propCode, newValue) {
            this.equipTypeGroups.forEach((group) => {
                const equip = group.equips.find((e) => e.equipIdx === equipIdx);
                if (equip) {
                    const prop = equip.ioProps.find((p) => p.propCode === propCode);
                    if (prop) {
                        this.$set(prop, "ptVal", newValue);
                        this.$set(prop, "status", this.classifyStatus(prop));
                    }
                }
            });
        },

        // 상태 분류 (xeEquipmentControl와 동일)
        classifyStatus(prop) {
            const code = (prop.propCode || "").toUpperCase();
            const value = Number(prop.ptVal);
            const isNumber = !isNaN(value);

            if (code.includes("ONOFF")) {
                return value === 1 ? "active" : "normal";
            } else if (code.includes("TEMP")) {
                if (!isNumber) return "abnormal";
                if (value >= 35) return "danger";
                if (value >= 30 || value < 10) return "warning";
                return "normal";
            } else if (code.includes("CO2")) {
                if (!isNumber) return "abnormal";
                if (value >= 2000) return "danger";
                if (value >= 1000) return "warning";
                return "normal";
            } else if (code.includes("HUMI")) {
                if (!isNumber) return "abnormal";
                if (value < 30 || value > 70) return "danger";
                if (value < 40 || value > 60) return "warning";
                return "normal";
            } else if (code.includes("PM10")) {
                if (!isNumber) return "abnormal";
                if (value > 150) return "danger";
                if (value > 80) return "warning";
                if (value > 30) return "normal";
                return "good";
            }

            return "normal";
        },

        // 값 포맷팅 (encodeDesc 적용)
        formatPropValue(prop) {
            // equipTypeInfo에서 해당 propCode의 메타데이터 찾기
            let propInfo = null;

            // 현재 설비가 속한 설비 유형 찾기
            for (const group of this.equipTypeGroups) {
                const equip = group.equips.find((e) =>
                    e.ioProps.some((p) => p.propCode === prop.propCode && p.ptAddr === prop.ptAddr)
                );
                if (equip) {
                    const equipTypeInfo = this.widget.equipTypeInfoMap[group.equipType];
                    propInfo = equipTypeInfo?.ioProps?.find((p) => p.propCode === prop.propCode);
                    break;
                }
            }

            return this.formatValueWithEncode(prop.ptVal, propInfo, prop.unit);
        },

        // encodeDesc를 사용한 값 포맷팅
        formatValueWithEncode(value, propInfo, unit) {
            // 값이 없는 경우
            if (value === undefined || value === null) {
                return "N/A";
            }

            // propInfo가 없으면 기본 포맷팅
            if (!propInfo) {
                const unitText = this.getUnitText(unit);
                return unitText ? `${value} ${unitText}` : `${value}`;
            }

            // Enum 타입 처리
            if (propInfo.encodeType === "Enum" && propInfo.encodeDesc) {
                try {
                    const enumValues = JSON.parse(propInfo.encodeDesc);
                    const numericValue = parseFloat(value);
                    if (!isNaN(numericValue) && enumValues[numericValue] !== undefined) {
                        return enumValues[numericValue];
                    }
                } catch (error) {
                    console.error("Error parsing enum values:", error);
                }
            }

            // Range/Number 타입 처리 (단위 포함)
            if ((propInfo.encodeType === "Range" || propInfo.encodeType === "Number") && propInfo.unit) {
                const unitText = this.getUnitText(propInfo.unit);
                return `${value} ${unitText}`;
            }

            // 기본 처리 (단위만 추가)
            if (propInfo.unit) {
                const unitText = this.getUnitText(propInfo.unit);
                return `${value} ${unitText}`;
            }

            return `${value}`;
        },

        // 단위 텍스트 변환
        getUnitText(unit) {
            let rtnText = "";
            if (unit && this.units) {
                rtnText = this.units.find((code) => code.value === unit)?.text || unit;
            }
            return rtnText;
        },

        // 툴팁 생성
        getTooltip(prop) {
            const unit = this.getUnitText(prop.unit);
            return `${prop.propName}: ${prop.ptVal} ${unit}`.trim();
        },

        // 전체 상태 분류
        getOverallStatus(ioProps) {
            if (!ioProps || ioProps.length === 0) {
                return "status-normal";
            }

            const hasDanger = ioProps.some((item) => item.status === "danger");
            if (hasDanger) return "status-danger";

            const hasWarning = ioProps.some((item) => item.status === "warning");
            if (hasWarning) return "status-warning";

            const hasAbnormal = ioProps.some((item) => item.status === "abnormal");
            if (hasAbnormal) return "status-abnormal";

            const hasActive = ioProps.some((item) => item.status === "active");
            if (hasActive) return "status-active";

            const hasGood = ioProps.some((item) => item.status === "good");
            if (hasGood) return "status-good";

            return "status-normal";
        },

        // 설비 유형 아이콘
        getEquipTypeIcon(equipType) {
            return getEquipTypeIconFromStore(this.$store, equipType);
        },

        // 설비 카드 그리드 스타일 (위젯 넓이에 따라 동적 열 개수)
        getEquipGridStyle(equipCount) {
            if (equipCount === 1) {
                return {
                    gridTemplateColumns: "1fr",
                };
            } else if (equipCount === 2) {
                return {
                    gridTemplateColumns: "repeat(2, 1fr)",
                };
            } else {
                // 3개 이상: 위젯 넓이에 따라 열 개수 결정
                let columns = 3; // 기본 3열

                if (this.width >= 18) {
                    columns = 5; // 매우 넓음: 5열
                } else if (this.width >= 14) {
                    columns = 4; // 넓음: 4열
                } else if (this.width >= 10) {
                    columns = 3; // 보통: 3열
                } else {
                    columns = 2; // 좁음: 2열
                }

                return {
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    gridTemplateRows: "auto",
                };
            }
        },

        // 컴팩트 모드 - 속성 그리드 스타일
        getPropGridStyleCompact(propCount) {
            if (propCount === 1) {
                return {
                    gridTemplateColumns: "1fr",
                };
            } else {
                // 2개 이상: 2열 그리드
                return {
                    gridTemplateColumns: "repeat(2, 1fr)",
                };
            }
        },

        // 타일 모드 - 속성 그리드 스타일
        getPropGridStyleTile(propCount) {
            if (propCount === 1) {
                return {
                    gridTemplateColumns: "1fr",
                };
            } else {
                // 2개 이상: 2열 그리드
                return {
                    gridTemplateColumns: "repeat(2, 1fr)",
                };
            }
        },

        // 배경 스타일
        getBackgroundStyle() {
            if (this.widget.panel && this.widget.panel.backgroundColor) {
                const baseColor = this.widget.panel.backgroundColor;

                if (baseColor.startsWith("#")) {
                    const darkerColor = this.adjustColor(baseColor, -30);
                    const darkestColor = this.adjustColor(baseColor, -60);

                    return {
                        background: `linear-gradient(45deg, ${darkestColor} 0%, ${darkerColor} 85%, ${baseColor} 100%)`,
                    };
                } else {
                    return {
                        background: baseColor,
                    };
                }
            }
            return {};
        },

        // 색상 조정
        adjustColor(color, amount) {
            const hex = color.replace("#", "");
            const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
            const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
            const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
            return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
        },

        // 모달 관련
        onModalCancel() {
            this.$refs.modalSetting.hide();
        },

        onModalOK(setting) {
            const updatedWidget = Object.assign({}, this.widget, setting);

            this.$refs.modalSetting.hide();

            // 설정이 변경되었음을 부모 컴포넌트에 알림
            this.$emit("on-change-widget", updatedWidget);

            // 즉시 반영을 위해 widget 객체 직접 업데이트
            this.$set(this.widget, "selectedEquips", setting.selectedEquips);
            this.$set(this.widget, "equipStatusData", setting.equipStatusData);
            this.$set(this.widget, "equipTypeInfoMap", setting.equipTypeInfoMap);
            if (setting.panel) {
                this.$set(this.widget, "panel", setting.panel);
            }

            // 데이터 재구성
            this.$nextTick(() => {
                this.applyData();
            });
        },
    },
};
</script>

<style scoped>
.kbet-widget-status {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px;
}

.kbet-widget-status::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
        radial-gradient(circle at 20% 80%, rgba(52, 152, 219, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(46, 204, 113, 0.08) 0%, transparent 50%);
    pointer-events: none;
}

/* ========== 컴팩트 모드 (리스트 형태) ========== */
.equip-type-section-compact {
    margin-bottom: 16px;
    position: relative;
}

.equip-type-section-compact:last-child {
    margin-bottom: 0;
}

.equip-list-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.equip-list-item {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 10px 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.equip-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.equip-list-name {
    font-weight: 600;
    color: #fff;
    font-size: 12px;
}

.equip-list-props {
    display: grid;
    gap: 6px;
    /* gridTemplateColumns는 동적으로 설정됨 */
}

.prop-tag {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 6px 8px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 600;
    border-left: 3px solid;
    background: rgba(255, 255, 255, 0.05);
    gap: 2px;
}

.prop-tag-label {
    font-size: 9px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
}

.prop-tag-value {
    font-size: 11px;
    font-weight: 700;
}

/* prop-tag 상태별 색상 */
.prop-tag.status-active {
    background: rgba(46, 204, 113, 0.15);
    border-left-color: #2ecc71;
}

.prop-tag.status-active .prop-tag-value {
    color: #2ecc71;
}

.prop-tag.status-warning {
    background: rgba(255, 171, 0, 0.15);
    border-left-color: #ffab00;
}

.prop-tag.status-warning .prop-tag-value {
    color: #ffab00;
}

.prop-tag.status-danger {
    background: rgba(213, 0, 0, 0.15);
    border-left-color: #d50000;
}

.prop-tag.status-danger .prop-tag-value {
    color: #d50000;
}

.prop-tag.status-good {
    background: rgba(52, 152, 219, 0.15);
    border-left-color: #3498db;
}

.prop-tag.status-good .prop-tag-value {
    color: #3498db;
}

.prop-tag.status-normal {
    background: rgba(77, 208, 225, 0.15);
    border-left-color: #4dd0e1;
}

.prop-tag.status-normal .prop-tag-value {
    color: #4dd0e1;
}

.prop-tag.status-abnormal {
    background: rgba(236, 239, 241, 0.15);
    border-left-color: #eceff1;
}

.prop-tag.status-abnormal .prop-tag-value {
    color: #eceff1;
}

.no-props-inline {
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    font-style: italic;
}

/* ========== 타일 모드 (그리드 형태) ========== */
.equip-type-section {
    margin-bottom: 16px;
    position: relative;
}

.equip-type-section:last-child {
    margin-bottom: 0;
}

/* 설비 유형 뱃지 */
.type-badge-container {
    margin-bottom: 8px;
}

.type-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.15);
    padding: 6px 12px;
    border-radius: 16px;
    color: #fff;
    font-weight: 600;
    font-size: 12px;
    border: 1px solid rgba(255, 255, 255, 0.25);
}

.type-icon {
    font-size: 14px;
    color: #fff;
}

.type-name {
    font-size: 13px;
    color: #fff;
}

.equip-count-badge {
    background: rgba(0, 0, 0, 0.2);
    color: #fff;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 700;
}

/* 설비 타일 컨테이너 */
.equip-tiles-container {
    display: grid;
    gap: 10px;
    /* grid-template-columns와 rows는 동적으로 설정됨 */
}

/* 설비 타일 */
.equip-tile {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    min-width: 0;
    min-height: 0;
}

/* 설비 타일 헤더 */
.equip-tile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.equip-tile-name {
    font-weight: 600;
    color: #fff;
    font-size: 12px;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
}

.status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.status-indicator.status-active {
    background-color: #2ecc71;
    box-shadow: 0 0 8px #2ecc7160;
}

.status-indicator.status-warning {
    background-color: #ffab00;
    box-shadow: 0 0 8px #ffab0060;
}

.status-indicator.status-danger {
    background-color: #d50000;
    box-shadow: 0 0 8px #d5000060;
}

.status-indicator.status-good {
    background-color: #3498db;
    box-shadow: 0 0 8px #3498db60;
}

.status-indicator.status-abnormal {
    background-color: #eceff1;
    box-shadow: 0 0 8px #eceff160;
}

.status-indicator.status-normal {
    background-color: #4dd0e1;
    box-shadow: 0 0 8px #4dd0e160;
}

/* 속성 리스트 (타일 모드) */
.prop-list {
    display: grid;
    gap: 6px;
    /* gridTemplateColumns는 동적으로 설정됨 */
}

.prop-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 6px 8px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.05);
    border-left: 3px solid;
    gap: 2px;
}

.prop-item-label {
    font-size: 9px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
}

.prop-item-value {
    font-size: 11px;
    font-weight: 700;
    color: #fff;
}

/* prop-item 상태별 색상 */
.prop-item.status-active {
    border-left-color: #2ecc71;
}

.prop-item.status-active .prop-item-value {
    color: #2ecc71;
}

.prop-item.status-warning {
    border-left-color: #ffab00;
}

.prop-item.status-warning .prop-item-value {
    color: #ffab00;
}

.prop-item.status-danger {
    border-left-color: #d50000;
}

.prop-item.status-danger .prop-item-value {
    color: #d50000;
}

.prop-item.status-good {
    border-left-color: #3498db;
}

.prop-item.status-good .prop-item-value {
    color: #3498db;
}

.prop-item.status-normal {
    border-left-color: #4dd0e1;
}

.prop-item.status-normal .prop-item-value {
    color: #4dd0e1;
}

.prop-item.status-abnormal {
    border-left-color: #eceff1;
}

.prop-item.status-abnormal .prop-item-value {
    color: #eceff1;
}

/* 속성 없음 */
.no-props {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    gap: 8px;
}

.no-props i {
    font-size: 20px;
}

/* 설비 없음 */
.no-equips {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: rgba(255, 255, 255, 0.6);
}

.no-equips-icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.no-equips-text {
    font-size: 14px;
    font-weight: 600;
}

/* 툴팁 */
::v-deep .tooltip-inner {
    white-space: pre-line;
    text-align: left;
    font-size: 12px;
    line-height: 1.6;
}
</style>
